
import React, { useState } from 'react';
import { Job, Crew, JobStatus, JobBriefing as JobBriefingType, RiskAssessment as RiskAssessmentType, TreeSurvey as TreeSurveyType } from '../types';
import JobBriefing from './JobBriefing';
import RiskAssessment from './RiskAssessment';
import TreeSurvey from './TreeSurvey';
import EstimatedEarningsCalculator from './EstimatedEarningsCalculator';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '../lib/firestore-errors';
import { sanitizeForFirestore } from '../lib/utils';

interface Props {
  jobs: Job[];
  crews: Crew[];
  onUpdate: (job: Job) => void;
}

const CrewDashboardView: React.FC<Props> = ({ jobs, crews, onUpdate }) => {
  const activeJobs = jobs.filter(j => j.status === JobStatus.SCHEDULED || j.status === JobStatus.IN_PROGRESS);

  const [activeBriefing, setActiveBriefing] = useState<Job | null>(null);
  const [activeRisk, setActiveRisk] = useState<Job | null>(null);
  const [activeSurvey, setActiveSurvey] = useState<Job | null>(null);

  const toggleSafety = (job: Job) => {
    onUpdate({ ...job, safetyChecked: !job.safetyChecked });
  };

  const handleSaveBriefing = async (briefing: Omit<JobBriefingType, 'id' | 'createdAt'>) => {
    try {
      await addDoc(collection(db, 'jobBriefings'), sanitizeForFirestore({
        ...briefing,
        createdAt: new Date().toISOString()
      }));
      setActiveBriefing(null);
    } catch (e) {
      handleFirestoreError(e, OperationType.WRITE, 'jobBriefings');
    }
  };

  const handleSaveRisk = async (assessment: Omit<RiskAssessmentType, 'id' | 'createdAt'>) => {
    try {
      await addDoc(collection(db, 'riskAssessments'), sanitizeForFirestore({
        ...assessment,
        createdAt: new Date().toISOString()
      }));
      setActiveRisk(null);
    } catch (e) {
      handleFirestoreError(e, OperationType.WRITE, 'riskAssessments');
    }
  };

  const handleSaveSurvey = async (survey: Omit<TreeSurveyType, 'id' | 'createdAt'>) => {
    try {
      await addDoc(collection(db, 'treeSurveys'), sanitizeForFirestore({
        ...survey,
        createdAt: new Date().toISOString()
      }));
      setActiveSurvey(null);
    } catch (e) {
      handleFirestoreError(e, OperationType.WRITE, 'treeSurveys');
    }
  };

  return (
    <div className="space-y-6">
      <EstimatedEarningsCalculator jobs={activeJobs} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {activeJobs.map(job => (
          <div key={job.id} className="bg-white rounded-2xl border-2 border-stone-200 shadow-sm overflow-hidden flex flex-col">
            <div className={`p-4 ${job.safetyChecked ? 'bg-gecko text-white' : 'bg-orange-500 text-white'}`}>
              <div className="flex justify-between items-center">
                <span className="font-bold text-sm tracking-widest uppercase">Work Order</span>
                <span className="text-xs font-bold">{job.safetyChecked ? '✓ Ready' : '! Safety Check Needed'}</span>
              </div>
              <h4 className="text-xl font-bold mt-2">{job.clientName}</h4>
            </div>
            
            <div className="p-6 space-y-4 flex-1">
              <div>
                <p className="text-xs font-bold text-stone-400 uppercase">Location</p>
                <p className="font-medium text-bark">{job.address}</p>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.address)}`}
                  target="_blank"
                  className="text-palm font-bold text-sm hover:underline mt-1 inline-block"
                >
                  Open in Maps →
                </a>
              </div>

              <div>
                <p className="text-xs font-bold text-stone-400 uppercase mb-2">Service Scope</p>
                <ul className="space-y-1">
                  {(job.lineItems || []).map(li => (
                    <li key={li.id} className="flex items-center space-x-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-gecko" />
                      <span>{li.quantity}x {li.description}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {job.attachments && job.attachments.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-stone-400 uppercase mb-2">Photos & Diagrams</p>
                  <div className="grid grid-cols-2 gap-2">
                    {job.attachments.map((src, idx) => (
                      <a 
                        key={idx} 
                        href={src} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block rounded-lg overflow-hidden border border-stone-200 hover:border-palm transition"
                      >
                        <img src={src} alt={`Attachment ${idx + 1}`} className="w-full h-24 object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 pt-0 border-t border-stone-100 mt-auto flex flex-col space-y-3">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <button 
                  onClick={() => setActiveBriefing(job)}
                  className="py-2 bg-stone-100 text-stone-600 rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-stone-200 transition"
                >
                  Job Briefing
                </button>
                <button 
                  onClick={() => setActiveRisk(job)}
                  className="py-2 bg-stone-100 text-stone-600 rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-stone-200 transition"
                >
                  Risk Assess
                </button>
                <button 
                  onClick={() => setActiveSurvey(job)}
                  className="col-span-2 py-2 bg-stone-100 text-stone-600 rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-stone-200 transition"
                >
                  Tree Survey (BS5837)
                </button>
              </div>

              <button 
                onClick={() => toggleSafety(job)}
                className={`w-full py-3 rounded-xl font-bold border-2 transition ${
                  job.safetyChecked 
                  ? 'border-gecko text-gecko bg-white hover:bg-gecko hover:text-white' 
                  : 'bg-stone-100 text-bark border-stone-100 hover:bg-stone-200'
                }`}
              >
                {job.safetyChecked ? 'Edit Safety Checklist' : 'Start Safety Inspection'}
              </button>
              
              {job.safetyChecked && (
                <button 
                  onClick={() => onUpdate({...job, status: JobStatus.COMPLETED})}
                  className="w-full py-3 bg-palm text-white rounded-xl font-bold shadow-lg shadow-palm/20 hover:scale-[1.02] transition"
                >
                  Finish Job & Upload Docs
                </button>
              )}
            </div>
          </div>
        ))}
        {activeJobs.length === 0 && (
          <div className="col-span-full py-20 text-center bg-white rounded-2xl border border-stone-200">
            <p className="text-stone-400 font-bold text-lg">No active work orders for today.</p>
          </div>
        )}
      </div>

      {activeBriefing && (
        <JobBriefing 
          job={activeBriefing} 
          onComplete={handleSaveBriefing} 
          onClose={() => setActiveBriefing(null)} 
        />
      )}
      {activeRisk && (
        <RiskAssessment 
          job={activeRisk} 
          onComplete={handleSaveRisk} 
          onClose={() => setActiveRisk(null)} 
        />
      )}
      {activeSurvey && (
        <TreeSurvey 
          job={activeSurvey} 
          onComplete={handleSaveSurvey} 
          onClose={() => setActiveSurvey(null)} 
        />
      )}
    </div>
  );
};

export default CrewDashboardView;
